import { sha256 } from '@noble/hashes/sha2.js';
import { toBase64 } from '../vault/bytes';
import { clearSession, setSession } from './vaultSession';
import type { VaultSession } from './vaultSession';
import { setAuth } from './authSession';
import type { AuthSession } from '../auth/firebaseAuth';

/**
 * How a second tab gets in without answering the guard again.
 *
 * A browser that has already unlocked holds the dataKey in a tab's memory, and
 * another tab of the same browser is the same trust boundary — same origin,
 * same profile, same person. It was still paying a fingerprint and a manifest
 * round trip, which is what made opening anything in a new tab feel heavy.
 *
 * BroadcastChannel is the carry, and it is also the security argument: the
 * channel is same-origin by construction, so the audience is exactly the set of
 * contexts that could already read the key out of a sibling tab's heap. Nothing
 * is written down. A key at rest is readable by any script that ever lands on
 * this origin and outlives the browser being closed, which is a different and
 * worse thing — see the note in `vaultSession`.
 *
 * The one real widening, accepted knowingly: the key becomes reachable from a
 * context that never answered the guard itself. It cannot be reached from a
 * browser where no tab is open, because there is nobody to answer — so the
 * first tab of a session still asks, which is wanted rather than a limitation.
 */

export interface Handover {
  session: VaultSession;
  /**
   * The signed-in account, which the vault keys are useless without.
   *
   * Every gated read presents an ID token as well as the grant, so a tab
   * handed only the keys holds a vault it cannot read from. It has no way to
   * resume the account itself either: that happens in the unlock, and adopting
   * exists precisely to skip the unlock. Null only where the answering tab is
   * somehow without one, which the asker treats as nothing offered.
   */
  account: AuthSession | null;
  /**
   * What the answering tab had already synced. The asker uses it to open the
   * cached manifest at that exact version and paint before the network
   * answers; a version it cannot find in the cache simply costs it nothing.
   */
  manifestVersion: number;
}

const CHANNEL = 'pondering.vault.handover';

/**
 * Long enough for a backgrounded tab to be woken and answer, short enough to
 * be invisible in front of a guard prompt — which is what the wait replaces
 * when nobody answers. A silent 400ms before a fingerprint sheet costs less
 * than the sheet does.
 */
const WAIT_MS = 400;

type Ask = { kind: 'ask'; id: string };
type Offer = {
  kind: 'offer';
  id: string;
  dataKey: Uint8Array;
  grantSecret: Uint8Array;
  manifestVersion: number;
  account: AuthSession | null;
};
type Forget = { kind: 'forget' };
/** [grant] names the refused grant by digest: the channel carries keys only
 *  inside an offer, to the tab that asked for one. */
type CutOff = { kind: 'cut-off'; grant: string };
type Message = Ask | Offer | Forget | CutOff;

const grantName = (grantSecret: Uint8Array): string => toBase64(sha256(grantSecret));

/** Absent in older Safari and in the test environment; every path here has to
 *  degrade to "nobody answered" rather than to a thrown page. */
function channel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null;
  try {
    return new BroadcastChannel(CHANNEL);
  } catch {
    return null;
  }
}

/**
 * Asks whatever tabs are open whether one of them is unlocked, and resolves
 * with the first answer or with null.
 *
 * The id is not decoration: opening several links at once means several tabs
 * asking at the same moment, and an answer carries the id of the ask it
 * belongs to so no tab consumes one meant for another.
 */
export function askPeers(timeoutMs: number = WAIT_MS): Promise<Handover | null> {
  const bus = channel();
  if (!bus) return Promise.resolve(null);

  const id = crypto.randomUUID();

  return new Promise<Handover | null>((resolve) => {
    let settled = false;
    const finish = (handover: Handover | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      bus.close();
      resolve(handover);
    };

    const timer = setTimeout(() => finish(null), timeoutMs);

    bus.onmessage = (event: MessageEvent<Message>) => {
      const message = event.data;
      // A browser being forgotten mid-ask must not be let in by the answer
      // that was already in flight.
      if (message?.kind === 'forget') return finish(null);
      if (message?.kind !== 'offer' || message.id !== id) return;
      finish({
        session: { dataKey: message.dataKey, grantSecret: message.grantSecret },
        manifestVersion: message.manifestVersion,
        account: message.account ?? null,
      });
    };

    bus.postMessage({ kind: 'ask', id } satisfies Ask);
  });
}

/**
 * Answers other tabs' asks for as long as this one holds a session.
 *
 * [supply] is consulted per ask rather than captured once, so a tab that is
 * still opening — or one whose session was cleared — answers nothing instead
 * of handing over a key it no longer has. A browser cannot answer its own ask:
 * BroadcastChannel does not deliver to the context that posted.
 *
 * The key material crosses as a structured clone, so the receiving tab gets
 * its own copy and `clearSession` zeroing one does not reach the other.
 *
 * [onCutOff] runs when a sibling found the grant this tab is serving refused.
 * A tab on some other grant — one paired again since — is not told.
 */
export function servePeers(supply: () => Handover | null, onCutOff?: () => void): () => void {
  const bus = channel();
  if (!bus) return () => {};

  bus.onmessage = (event: MessageEvent<Message>) => {
    const message = event.data;
    // Forgetting a browser has to reach every tab of it. Otherwise the tab
    // that ran the command clears itself while its siblings keep the key AND
    // keep answering, so the next tab is let straight back in and the command
    // silently did not stick.
    if (message?.kind === 'forget') return clearSession();
    if (message?.kind === 'cut-off') {
      const held = supply();
      if (held && grantName(held.session.grantSecret) === message.grant) onCutOff?.();
      return;
    }
    if (message?.kind !== 'ask') return;
    const handover = supply();
    if (!handover) return;
    bus.postMessage({
      kind: 'offer',
      id: message.id,
      dataKey: handover.session.dataKey,
      grantSecret: handover.session.grantSecret,
      manifestVersion: handover.manifestVersion,
      account: handover.account,
    } satisfies Offer);
  };

  return () => bus.close();
}

/**
 * Tells every other tab of this browser to drop what it holds.
 *
 * Posted rather than awaited: the tab that runs the command clears itself, and
 * a sibling that never hears this is one whose page is gone anyway.
 */
export function announceForget(): void {
  const bus = channel();
  if (!bus) return;
  bus.postMessage({ kind: 'forget' } satisfies Forget);
  bus.close();
}

/**
 * Tells the other tabs of this browser that [grantSecret] was refused.
 *
 * A hidden tab does not probe, so without this it goes on holding the dead key
 * and answering asks with it — and a reload anywhere is handed a key that is
 * refused on its first read.
 */
export function announceCutOff(grantSecret: Uint8Array): void {
  const bus = channel();
  if (!bus) return;
  bus.postMessage({ kind: 'cut-off', grant: grantName(grantSecret) } satisfies CutOff);
  bus.close();
}

/**
 * Asks, and opens this tab on whatever a sibling hands back. True when the tab
 * is now connected.
 *
 * Both ways in need this and neither is the obvious one. A REMEMBERED browser
 * would otherwise go to its guard, which is the annoyance being removed; a
 * browser paired for the session only has no guard to go to at all and would
 * send someone back to their phone while the vault sits open one tab away.
 */
export async function adoptFromPeer(): Promise<boolean> {
  const handover = await askPeers();
  if (!handover) return false;
  offerWarmManifestVersion(handover.manifestVersion);
  // BEFORE the session, because opening the vault is what starts the reads
  // that need it: [setSession] publishes synchronously and the store syncs off
  // that, so an account arriving afterwards is already too late for the first
  // read. A tab that adopted without one held a working key and no token, and
  // every read threw `not signed in` where nothing was catching.
  if (handover.account) setAuth(handover.account);
  setSession(handover.session);
  return true;
}

/**
 * The manifest version a handover arrived with, readable exactly once.
 *
 * It is passed this way rather than on the session because it is not part of
 * what a tab HOLDS — it is a hint about what it can skip on the way in, true
 * only for the open that immediately follows. Taking it clears it, so a later
 * re-read does a real sync rather than warm-starting from a version that has
 * long since moved.
 */
let warmVersion: number | null = null;

export function offerWarmManifestVersion(version: number): void {
  warmVersion = version;
}

export function takeWarmManifestVersion(): number | null {
  const version = warmVersion;
  warmVersion = null;
  return version;
}
